/**
 * App Registry (Dynamic)
 *
 * Soporta dos modos de carga:
 * 1. Remote (Module Federation) - apps externas cargadas desde URL
 * 2. Local - apps internas que aun no han sido migradas a remotes
 *
 * Las apps remotas se registran dinamicamente por useEntitlements
 * despues de obtener los datos de Firestore.
 */

import { lazy, ComponentType, LazyExoticComponent } from 'react'
import { createRemoteApp, RemoteAppConfig } from '@/lib/remote-loader'
import type { AppProps } from '@summafx/types'

export type { AppProps }

// Todas las apps se cargan via Module Federation (remote).
// LOCAL_APPS queda vacio — solo como fallback si alguna app necesita ser embebida temporalmente.
const LOCAL_APPS: Record<string, { component: () => Promise<{ default: ComponentType<AppProps> }> }> = {
}

// Cache para lazy components - evita remounts
const lazyCache = new Map<string, LazyExoticComponent<ComponentType<AppProps>>>()

// Registro dinamico de apps remotas (poblado por useEntitlements)
const remoteConfigs = new Map<string, RemoteAppConfig>()

/**
 * Registra apps remotas disponibles.
 * Llamado por useEntitlements despues del fetch de entitlements.
 *
 * Apps sin remote_url se ignoran (deben estar en LOCAL_APPS o son errores de config).
 * Skip re-registration if configs haven't changed (preserves lazy component references).
 */
export function registerRemoteApps(
  apps: Array<{ appId: string; remoteUrl: string; scope: string; module: string }>
) {
  // Check if configs are identical — skip to avoid destroying mounted lazy components
  let changed = apps.length !== remoteConfigs.size
  if (!changed) {
    for (const app of apps) {
      const existing = remoteConfigs.get(app.appId)
      if (!existing || existing.remoteUrl !== app.remoteUrl || existing.scope !== app.scope || existing.module !== app.module) {
        changed = true
        break
      }
    }
  }
  if (!changed) return

  remoteConfigs.clear()
  lazyCache.clear()
  for (const app of apps) {
    if (app.remoteUrl) {
      remoteConfigs.set(app.appId, {
        remoteUrl: app.remoteUrl,
        scope: app.scope,
        module: app.module,
      })
    } else if (!LOCAL_APPS[app.appId]) {
      console.error(
        `[AppRegistry] MISCONFIGURED: "${app.appId}" has no remote_url in Firestore and no LOCAL_APPS entry. ` +
        `This app will NOT load. Fix remote_url in Firebase Console > applications/${app.appId}`
      )
    }
  }
}

/**
 * Prefetch remoteEntry.js scripts so they're in the browser cache
 * when Module Federation needs them. Called with cached entitlements
 * data before the fresh API response arrives.
 */
export function prefetchRemoteEntries(remoteUrls: string[]) {
  for (const url of remoteUrls) {
    if (!url) continue
    // Avoid duplicate prefetch links
    if (document.querySelector(`link[href="${url}"]`)) continue
    const link = document.createElement('link')
    link.rel = 'prefetch'
    link.as = 'script'
    link.href = url
    document.head.appendChild(link)
  }
}

/**
 * Get lazy component for an app.
 * Priority: cache > remote (Module Federation) > local fallback
 */
export function getAppComponent(appId: string) {
  // 1. Check cache
  let cached = lazyCache.get(appId)
  if (cached) return cached

  // 2. Check remote config (Module Federation)
  const config = remoteConfigs.get(appId)
  if (config) {
    cached = createRemoteApp(config)
    lazyCache.set(appId, cached)
    return cached
  }

  // 3. Check local fallback (apps no migradas aun)
  const local = LOCAL_APPS[appId]
  if (local) {
    cached = lazy(local.component)
    lazyCache.set(appId, cached)
    return cached
  }

  console.warn(`[AppRegistry] App ${appId} not found (no remote, no local)`)
  return null
}
